<template>
  <div
    v-bind="rootAttrs"
    :class="rootClasses"
    :data-testid="optionDataTestid"
    :title="title"
    :role="rootRole"
    :aria-selected="rootAriaSelected"
    :aria-disabled="isDisabled ? 'true' : undefined"
    :tabindex="rootTabIndex"
    @click="handleSelect"
    @keydown.enter.prevent="handleSelect"
    @keydown.space.prevent="handleSelect"
  >
    <WChecklistItem
      :model-value="isSelected"
      :control="control"
      :disabled="isDisabled"
      :section-tag="contentSectionTag"
      :selected="isSelected"
      :subtitle="subtitle"
      :title="title"
      :value="checklistValue"
      :variant="variant"
      appearance="list-item"
      class="w-autocomplete-list-item__checklist"
      selection-mode="delegated"
      @click.stop
      @select="handleChecklistSelect"
    >
      <template v-if="$slots.prepend" #prepend>
        <slot name="prepend" :item="item" :item-props="itemProps" :raw-item="rawItem" />
      </template>
      <template v-if="$slots.append" #append>
        <slot name="append" :item="item" :item-props="itemProps" :raw-item="rawItem" />
      </template>
    </WChecklistItem>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import WChecklistItem from './WChecklistItem.vue'
import type { WChecklistItemControl, WChecklistItemVariant } from './WChecklistItem.vue'
import { isAutocompleteValueSelected } from './autocompleteSelection'
import type { WAutocompleteItemKey } from './autocompleteSelection'
import type { WSelectionControlValue } from './WSelectionControl.types'

export interface WAutocompleteSlotItem {
  title?: string
  value?: unknown
  props?: Record<string, unknown>
  raw?: unknown
  type?: string
}

export type WAutocompleteListItemClickHandler = (event?: Event) => void
export type WAutocompleteListItemModelValue =
  | unknown[]
  | object
  | string
  | number
  | boolean
  | null
export type WAutocompleteListItemSubtitle = string | number | symbol | ((item: unknown) => unknown)
export type WAutocompleteListItemValueComparator = (value: unknown, optionValue: unknown) => boolean
export type WAutocompleteListItemDataTestid = string | ((item: WAutocompleteSlotItem) => string | undefined)

export interface WAutocompleteListItemProps {
  item: WAutocompleteSlotItem
  itemProps?: Record<string, unknown>
  itemTitle?: WAutocompleteItemKey | null
  itemValue?: WAutocompleteItemKey | null
  modelValue?: WAutocompleteListItemModelValue
  multiple?: boolean
  returnObject?: boolean
  sectionTag?: string
  itemSubtitle?: WAutocompleteListItemSubtitle
  valueComparator?: WAutocompleteListItemValueComparator
  optionDataTestid?: WAutocompleteListItemDataTestid
}

const props = withDefaults(defineProps<WAutocompleteListItemProps>(), {
  itemProps: () => ({}),
  itemTitle: undefined,
  itemValue: undefined,
  multiple: false,
  returnObject: false,
  sectionTag: 'unspecified',
  itemSubtitle: undefined,
  valueComparator: undefined,
  optionDataTestid: undefined
})

const ITEM_PROP_EXCLUSIONS = new Set(['active', 'class', 'data-testid', 'disabled', 'onClick', 'title', 'value'])

const rawItem = computed(() => props.item.raw ?? props.item)
const title = computed(() => String(props.item.title ?? props.itemProps.title ?? ''))
const optionValue = computed(() => {
  if (props.item.value === null) {
    return null
  }

  return props.item.value ?? props.itemProps.value
})
const control = computed<WChecklistItemControl>(() => (props.multiple ? 'checkbox' : 'text'))
const variant = computed<WChecklistItemVariant>(() => (subtitle.value ? 'two-line' : 'single-line'))
const isDisabled = computed(() => Boolean(props.itemProps.disabled ?? props.item.props?.disabled))
const itemClass = computed(() => props.itemProps.class)
const itemClickHandler = computed(() => props.itemProps.onClick)
const rootRole = computed(() => String(props.itemProps.role ?? 'option'))
const rootAriaSelected = computed<'true' | 'false'>(() => (isSelected.value ? 'true' : 'false'))
const rootTabIndex = computed(() => {
  const tabIndex = props.itemProps.tabindex ?? props.itemProps.tabIndex

  if (typeof tabIndex === 'number' || typeof tabIndex === 'string') {
    return tabIndex
  }

  return -1
})

const optionDataTestid = computed(() => {
  const customDataTestid = props.itemProps['data-testid']

  if (typeof customDataTestid === 'string') {
    return customDataTestid
  }

  if (typeof props.optionDataTestid === 'function') {
    return props.optionDataTestid(props.item)
  }

  if (typeof props.optionDataTestid === 'string') {
    return props.optionDataTestid
  }

  return props.sectionTag ? `${props.sectionTag}-option-${String(optionValue.value)}` : undefined
})

const contentSectionTag = computed(() => (optionDataTestid.value ? `${optionDataTestid.value}-content` : undefined))

const subtitle = computed(() => {
  const resolvedSubtitle = resolveSubtitle()
  return resolvedSubtitle === null || resolvedSubtitle === undefined ? undefined : String(resolvedSubtitle)
})

const isSelected = computed(() => {
  const selectedValues = Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue]

  return selectedValues.some((value) => isEqualValue(value, optionValue.value))
})

const checklistValue = computed<WSelectionControlValue | undefined>(() => {
  const value = optionValue.value

  if (isSelectionControlValue(value)) {
    return value
  }

  return title.value
})

const rootAttrs = computed(() => {
  const attrs: Record<string, unknown> = {}

  Object.entries(props.itemProps).forEach(([key, value]) => {
    if (!ITEM_PROP_EXCLUSIONS.has(key)) {
      attrs[key] = value
    }
  })

  return attrs
})

const rootClasses = computed(() => [
  'v-list-item',
  'v-list-item--link',
  'w-autocomplete-list-item',
  {
    'v-list-item--active': isSelected.value,
    'v-list-item--disabled': isDisabled.value,
    'w-autocomplete-list-item--active': Boolean(props.itemProps.active),
    'w-autocomplete-list-item--selected': isSelected.value
  },
  itemClass.value
])

function resolveSubtitle(): unknown {
  if (!props.itemSubtitle) {
    return undefined
  }

  if (typeof props.itemSubtitle === 'function') {
    return props.itemSubtitle(rawItem.value)
  }

  return getProperty(rawItem.value, props.itemSubtitle)
}

function getProperty(item: unknown, key: string | number | symbol): unknown {
  if (!isRecord(item)) {
    return undefined
  }

  return item[key]
}

function isRecord(value: unknown): value is Record<string | number | symbol, unknown> {
  return typeof value === 'object' && value !== null
}

function isSelectionControlValue(value: unknown): value is WSelectionControlValue {
  return ['string', 'number', 'boolean'].includes(typeof value)
}

function isEqualValue(value: unknown, option: unknown): boolean {
  return isAutocompleteValueSelected(
    value,
    option,
    { itemTitle: props.itemTitle, itemValue: props.itemValue, returnObject: props.returnObject },
    props.valueComparator
  )
}

function invokeClickHandler(handler: unknown, event?: Event) {
  if (Array.isArray(handler)) {
    handler.forEach((clickHandler) => invokeClickHandler(clickHandler, event))
    return
  }

  if (typeof handler === 'function') {
    const clickHandler = handler as WAutocompleteListItemClickHandler
    clickHandler(event)
  }
}

function handleSelect(event?: Event) {
  if (isDisabled.value) {
    return
  }

  invokeClickHandler(itemClickHandler.value, event)
}

function handleChecklistSelect() {
  handleSelect()
}
</script>

<style scoped lang="scss">
.w-autocomplete-list-item {
  display: flex;
  min-height: 0 !important;
  padding: 0 !important;
  cursor: pointer;
}

// Inside a menu the option root owns the rest, hover and focus surfaces (WDropdown's menu styles).
// The row paints only its selected highlight: its own hover is composited over an opaque white
// surface, which would otherwise cover the menu's hover layer. The `:hover` selector outranks the
// row's own hover rule; the keyboard-active rule below comes later, so it still wins.
.w-autocomplete-list-item :deep(.w-checklist-item:not(.w-checklist-item--selected)),
.w-autocomplete-list-item :deep(.w-checklist-item:hover:not(.w-checklist-item--selected)) {
  background: transparent;
}

.w-autocomplete-list-item--active:not(.w-autocomplete-list-item--selected) :deep(.w-checklist-item) {
  background-color: var(--color-background-surface-hover);
}

.w-autocomplete-list-item.v-list-item--disabled {
  cursor: default;
}

.w-autocomplete-list-item__checklist {
  width: 100%;
}
</style>
